import { useMutation, useQuery, useQueryClient } from '@tanstack/react-query'
import {
  adjustLoyaltyPoints,
  getLoyaltyMemberLogs,
  getLoyaltyMembers,
  getLoyaltyOverview,
  getLoyaltyRules,
  updateLoyaltyRules,
} from '../../../gateways/breadstack/loyaltyApis'
import type {
  LoyaltyAdjustPointsRequest,
  LoyaltyMemberLogsParams,
  LoyaltyMembersParams,
  LoyaltyRules,
} from '../../../types/loyalty'

export const loyaltyRulesQueryKey = (storeId: string) =>
  ['loyalty', 'rules', storeId] as const
export const loyaltyOverviewQueryKey = (storeId: string) =>
  ['loyalty', 'overview', storeId] as const
export const loyaltyMembersQueryKey = (
  storeId: string,
  params: LoyaltyMembersParams,
) => ['loyalty', 'members', storeId, params] as const
export const loyaltyMemberLogsQueryKey = (
  storeId: string,
  customerId: number,
  params: LoyaltyMemberLogsParams,
) => ['loyalty', 'member-logs', storeId, customerId, params] as const

export const useLoyaltyRulesQuery = (storeId: string) =>
  useQuery({
    queryKey: loyaltyRulesQueryKey(storeId),
    queryFn: () => getLoyaltyRules(storeId),
    enabled: Boolean(storeId),
  })

export const useLoyaltyOverviewQuery = (storeId: string) =>
  useQuery({
    queryKey: loyaltyOverviewQueryKey(storeId),
    queryFn: () => getLoyaltyOverview(storeId),
    enabled: Boolean(storeId),
  })

export const useLoyaltyMembersQuery = (
  storeId: string,
  params: LoyaltyMembersParams,
) =>
  useQuery({
    queryKey: loyaltyMembersQueryKey(storeId, params),
    queryFn: () => getLoyaltyMembers(storeId, params),
    enabled: Boolean(storeId),
  })

export const useLoyaltyMemberLogsQuery = (
  storeId: string,
  customerId: number,
  params: LoyaltyMemberLogsParams,
) =>
  useQuery({
    queryKey: loyaltyMemberLogsQueryKey(storeId, customerId, params),
    queryFn: () => getLoyaltyMemberLogs(storeId, customerId, params),
    enabled: Boolean(storeId) && Number.isFinite(customerId) && customerId > 0,
  })

export const useUpdateLoyaltyRules = (storeId: string) => {
  const queryClient = useQueryClient()
  return useMutation({
    mutationFn: (body: LoyaltyRules) => updateLoyaltyRules(storeId, body),
    onSuccess: () => {
      queryClient.invalidateQueries({ queryKey: loyaltyRulesQueryKey(storeId) })
      queryClient.invalidateQueries({
        queryKey: loyaltyOverviewQueryKey(storeId),
      })
    },
  })
}

// Admin points adjustment — fires the assumed POST endpoint and invalidates
// every cached view that displays the affected member's balance:
//  - members list (any param permutation)
//  - per-member logs feed (drawer body)
//  - overview KPIs (points-in-circulation, totals)
export const useAdjustLoyaltyPoints = (storeId: string) => {
  const queryClient = useQueryClient()
  return useMutation({
    mutationFn: ({
      customerId,
      body,
    }: {
      customerId: number
      body: LoyaltyAdjustPointsRequest
    }) => adjustLoyaltyPoints(storeId, customerId, body),
    onSuccess: (_data, variables) => {
      queryClient.invalidateQueries({
        queryKey: ['loyalty', 'members', storeId],
      })
      queryClient.invalidateQueries({
        queryKey: ['loyalty', 'member-logs', storeId, variables.customerId],
      })
      queryClient.invalidateQueries({
        queryKey: loyaltyOverviewQueryKey(storeId),
      })
    },
  })
}
